export type JsonObject = Record<string, unknown>;

export const MAX_JSON_RESPONSE_BYTES = 32 * 1024 * 1024;
export const REQUEST_TIMEOUT_MS = 30_000;
export const MAX_JSON_STREAM_CHUNKS = 131_072;

class ResponseTooLargeError extends Error {}
class ResponseTooFragmentedError extends Error {}

export function isJsonObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function hasString(value: JsonObject, key: string): boolean {
  return typeof value[key] === "string";
}

export function hasBoolean(value: JsonObject, key: string): boolean {
  return typeof value[key] === "boolean";
}

export function hasArray(value: JsonObject, key: string): boolean {
  return Array.isArray(value[key]);
}

export function hasFiniteNumber(value: JsonObject, key: string): boolean {
  return typeof value[key] === "number" && Number.isFinite(value[key]);
}

export function hasNullableString(value: JsonObject, key: string): boolean {
  return value[key] === null || typeof value[key] === "string";
}

export function objectField(value: JsonObject, key: string): JsonObject | undefined {
  const field = value[key];
  return isJsonObject(field) ? field : undefined;
}

function abortReason(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException("The operation was aborted.", "AbortError");
}

export async function withRequestTimeout<T>(
  callerSignal: AbortSignal | null | undefined,
  operation: (signal: AbortSignal) => Promise<T>,
  timeoutMs = REQUEST_TIMEOUT_MS,
): Promise<T> {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) throw new RangeError("timeoutMs must be a positive finite number");

  const controller = new AbortController();
  const abortFromCaller = () => controller.abort(callerSignal?.reason);
  if (callerSignal?.aborted) abortFromCaller();
  else callerSignal?.addEventListener("abort", abortFromCaller, { once: true });

  const timeout = setTimeout(
    () => controller.abort(new DOMException("The request timed out.", "TimeoutError")),
    timeoutMs,
  );
  let rejectForAbort: (() => void) | undefined;
  const aborted = new Promise<never>((_resolve, reject) => {
    rejectForAbort = () => reject(abortReason(controller.signal));
    controller.signal.addEventListener("abort", rejectForAbort, { once: true });
  });

  try {
    if (controller.signal.aborted) throw abortReason(controller.signal);
    return await Promise.race([operation(controller.signal), aborted]);
  } finally {
    clearTimeout(timeout);
    callerSignal?.removeEventListener("abort", abortFromCaller);
    if (rejectForAbort) controller.signal.removeEventListener("abort", rejectForAbort);
  }
}

function isJsonMediaType(value: string | null): boolean {
  const mediaType = parseMediaType(value);
  if (!mediaType) return false;
  if (mediaType === "application/json") return true;
  if (!mediaType.startsWith("application/")) return false;
  const subtype = mediaType.slice("application/".length);
  return subtype.length > "+json".length && subtype.endsWith("+json");
}

function parseMediaType(value: string | null): string | undefined {
  if (!value) return undefined;
  let index = skipOptionalWhitespace(value, 0);
  const type = readToken(value, index);
  if (!type || value[type.end] !== "/") return undefined;
  const subtype = readToken(value, type.end + 1);
  if (!subtype) return undefined;
  index = skipOptionalWhitespace(value, subtype.end);
  while (index < value.length) {
    if (value[index] !== ";") return undefined;
    index = skipOptionalWhitespace(value, index + 1);
    const name = readToken(value, index);
    if (!name) return undefined;
    index = skipOptionalWhitespace(value, name.end);
    if (value[index] !== "=") return undefined;
    index = skipOptionalWhitespace(value, index + 1);
    if (value[index] === '"') {
      index = quotedStringEnd(value, index);
      if (index < 0) return undefined;
    } else {
      const parameterValue = readToken(value, index);
      if (!parameterValue) return undefined;
      index = parameterValue.end;
    }
    index = skipOptionalWhitespace(value, index);
  }
  return `${type.value}/${subtype.value}`.toLowerCase();
}

function readToken(value: string, start: number): { value: string; end: number } | undefined {
  let end = start;
  while (end < value.length && isTokenCharacter(value[end])) end += 1;
  return end === start ? undefined : { value: value.slice(start, end), end };
}

function isTokenCharacter(value: string): boolean {
  return /^[!#$%&'*+\-.^_`|~0-9A-Za-z]$/.test(value);
}

function skipOptionalWhitespace(value: string, start: number): number {
  let index = start;
  while (value[index] === " " || value[index] === "\t") index += 1;
  return index;
}

function quotedStringEnd(value: string, start: number): number {
  let index = start + 1;
  while (index < value.length) {
    const code = value.charCodeAt(index);
    if (value[index] === '"') return index + 1;
    if (value[index] === "\\") {
      index += 1;
      if (index >= value.length || !isQuotedCharacter(value.charCodeAt(index))) return -1;
    } else if (!isQuotedCharacter(code) || value[index] === '"') {
      return -1;
    }
    index += 1;
  }
  return -1;
}

function isQuotedCharacter(code: number): boolean {
  return code === 0x09 || (code >= 0x20 && code !== 0x7f) || code >= 0x80;
}

function cancelResponseBody(response: Response, reason: string): void {
  void response.body?.cancel(reason).catch(() => undefined);
}

async function boundedResponseBytes(response: Response, maxBytes: number): Promise<Uint8Array> {
  if (!Number.isSafeInteger(maxBytes) || maxBytes <= 0) throw new RangeError("maxBytes must be a positive safe integer");

  const contentLength = response.headers.get("content-length")?.trim();
  if (contentLength && /^\d+$/.test(contentLength) && BigInt(contentLength) > BigInt(maxBytes)) {
    cancelResponseBody(response, "response_too_large");
    throw new ResponseTooLargeError();
  }
  if (!response.body) return new Uint8Array();

  const reader = response.body.getReader();
  const blockSize = Math.min(64 * 1024, maxBytes);
  const blocks: Uint8Array[] = [];
  let block = new Uint8Array(blockSize);
  let blockOffset = 0;
  let total = 0;
  let chunkCount = 0;
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      chunkCount += 1;
      if (chunkCount > MAX_JSON_STREAM_CHUNKS) {
        void reader.cancel("response_too_fragmented").catch(() => undefined);
        throw new ResponseTooFragmentedError();
      }
      if (!value.byteLength) continue;
      total += value.byteLength;
      if (total > maxBytes) {
        void reader.cancel("response_too_large").catch(() => undefined);
        throw new ResponseTooLargeError();
      }
      let valueOffset = 0;
      while (valueOffset < value.byteLength) {
        const byteCount = Math.min(block.byteLength - blockOffset, value.byteLength - valueOffset);
        block.set(value.subarray(valueOffset, valueOffset + byteCount), blockOffset);
        blockOffset += byteCount;
        valueOffset += byteCount;
        if (blockOffset === block.byteLength) {
          blocks.push(block);
          block = new Uint8Array(blockSize);
          blockOffset = 0;
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  if (blockOffset) blocks.push(block.slice(0, blockOffset));

  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const bufferedBlock of blocks) {
    bytes.set(bufferedBlock, offset);
    offset += bufferedBlock.byteLength;
  }
  return bytes;
}

export async function strictJsonEnvelope(
  response: Response,
  makeError: (status: number, code: string) => Error,
  fallbackCode: string,
  maxBytes = MAX_JSON_RESPONSE_BYTES,
): Promise<JsonObject> {
  if (!isJsonMediaType(response.headers.get("content-type"))) {
    cancelResponseBody(response, "invalid_media_type");
    throw makeError(response.status, "invalid_response");
  }

  let payload: unknown;
  try {
    const bytes = await boundedResponseBytes(response, maxBytes);
    payload = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes));
  } catch (error) {
    if (error instanceof ResponseTooLargeError) throw makeError(response.status, "response_too_large");
    if (error instanceof ResponseTooFragmentedError) throw makeError(response.status, "response_too_fragmented");
    if (error instanceof DOMException && (error.name === "AbortError" || error.name === "TimeoutError")) throw error;
    throw makeError(response.status, "invalid_response");
  }
  if (!isJsonObject(payload)) throw makeError(response.status, "invalid_response");
  const errorCode = typeof payload.error === "string" && payload.error ? payload.error : "";
  if (!response.ok) throw makeError(response.status, errorCode || fallbackCode);
  if (payload.ok !== true) throw makeError(response.status, errorCode || "invalid_response");
  return payload;
}
